import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createObject, FormFieldObject, sectionDataObject } from '@/lib/stores/objectsStore';
import { useToast } from '@/hooks/use-toast';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useGlossaryDataContext } from '@/providers/ObjectsDataProvider';
import { useGlossaryBlueprintsDataContext } from '@/providers/GlossaryBlueprintsDataProvider';
import { LoadingSpinner } from '@/components/loaders/loading-spinner';
import { Input } from '@/components/ui/InputDefault';
import { CategorySelection } from '@/components/wizards/glossary/CategorySelection';
import { SingleFieldDisplay } from '@/components/wizards/glossary/SingleFieldDisplay';
import { FormProgressBar } from '@/components/wizards/glossary/FormProgressBar';
import { FormNavigation } from '@/components/wizards/glossary/FormNavigation';
import { CategoryOption, WizardStep, FieldWithAnswer } from '@/components/wizards/glossary/FormWizardTypes';
import { GlossaryBlueprint } from '@/types-games';

/**
 * FormPage - Typeform-style wizard for creating objects
 * 
 * Flow:
 * 1. Select category (from database export mapping)
 * 2. Enter object name
 * 3. Fill fields one by one
 * 4. Submit to library
 */
export default function FormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { selectedGame } = useSimplifiedUserData();
  const { loadData } = useGlossaryDataContext();
  const { glossaryBlueprints } = useGlossaryBlueprintsDataContext();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('category');
  const [selectedCategory, setSelectedCategory] = useState<CategoryOption | null>(null);
  const [selectedBlueprint, setSelectedBlueprint] = useState<GlossaryBlueprint | null>(null);
  const [objectName, setObjectName] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [currentFieldIndex, setCurrentFieldIndex] = useState<number>(0);
  const [allFields, setAllFields] = useState<FieldWithAnswer[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Get optional folder parameter from URL
  const folderFromUrl = searchParams.get('folder');

  /**
   * Get icon for category (can be customized)
   */
  const getCategoryIcon = useCallback((categoryName: string): string => {
    const iconMap: Record<string, string> = {
      'Quest': 'fa-map-marked-alt',
      'Quests': 'fa-map-marked-alt',
      'Dialog': 'fa-comments',
      'Dialogs': 'fa-comments',
      'ThankWall': 'fa-heart',
      'GameStory': 'fa-book',
      'Character': 'fa-user',
      'Characters': 'fa-users',
      'Item': 'fa-cube',
      'Items': 'fa-cubes',
    };
    return iconMap[categoryName] || 'fa-folder';
  }, []);

  /**
   * Load category mapping from database export settings
   */
  const categories = useMemo((): CategoryOption[] => {
    if (!selectedGame?.settings?.exportTemplates) {
      return [];
    }

    const config = selectedGame.settings.exportTemplates as any;
    const template = config.templates?.find((t: any) => t.id === 'preset-roblox-database');
    
    if (!template?.categoryMapping) {
      return [];
    }

    // Convert categoryMapping to CategoryOption array
    // categoryMapping = { "Quest Template V2": "Quest", "Dialog": "Dialogs" }
    return Object.entries(template.categoryMapping).map(([templateName, categoryName]) => ({
      name: categoryName as string,
      templateName: templateName,
      icon: getCategoryIcon(categoryName as string),
      description: `Create a new ${categoryName}`,
    }));
  }, [selectedGame, getCategoryIcon]);

  /**
   * Handle category selection
   */
  const handleCategorySelect = useCallback((category: CategoryOption) => {
    // Find the blueprint that matches the template name
    const blueprint = glossaryBlueprints.find(bp => bp.name === category.templateName);
    
    if (!blueprint) {
      toast({
        title: 'Template Not Found',
        description: `Could not find template "${category.templateName}". Please check your blueprints.`,
        variant: 'destructive',
      });
      return;
    }

    setSelectedCategory(category);
    setSelectedBlueprint(blueprint);
    setCurrentStep('name');
  }, [glossaryBlueprints, toast]);

  /**
   * Extract all fields from blueprint sections
   */
  useEffect(() => {
    if (selectedBlueprint && currentStep === 'fields') {
      const fields: FieldWithAnswer[] = [];
      
      // Ensure data exists and is an array
      if (selectedBlueprint.data && Array.isArray(selectedBlueprint.data)) {
        selectedBlueprint.data.forEach((section: sectionDataObject) => {
          // Ensure section has fields array
          if (section.fields && Array.isArray(section.fields)) {
            section.fields.forEach((field: FormFieldObject) => {
              fields.push({
                field: field,
                sectionId: section.id,
                value: field.value || getDefaultValue(field),
                error: undefined,
              });
            });
          }
        });
      } else {
        // Log error if blueprint data is not in expected format
        console.error('Blueprint data is not an array:', selectedBlueprint);
        toast({
          title: 'Template Error',
          description: 'The selected template has an invalid structure. Please contact support.',
          variant: 'destructive',
        });
        setCurrentStep('category');
        return;
      }
      
      // Check if we have any fields
      if (fields.length === 0) {
        toast({
          title: 'No Fields Found',
          description: 'This template has no fields to fill. Please select a different template.',
          variant: 'destructive',
        });
        setCurrentStep('category');
        return;
      }
      
      setAllFields(fields);
      setCurrentFieldIndex(0);
    }
  }, [selectedBlueprint, currentStep, toast]);

  /**
   * Get default value for a field based on its type
   */
  const getDefaultValue = (field: FormFieldObject): any => {
    if (field.value !== undefined) return field.value;
    
    switch (field.type) {
      case 'boolean':
        return false;
      case 'number':
        return 0;
      case 'title':
      case 'textarea':
        return '';
      default:
        return field.multiple ? [] : '';
    }
  };

  /**
   * Handle name input validation and progression
   */
  const handleNameNext = useCallback(() => {
    if (!objectName.trim()) {
      setNameError('Please enter a name for the object');
      return;
    }
    setNameError('');
    setCurrentStep('fields');
  }, [objectName]);

  /**
   * Handle field value change
   */
  const handleFieldChange = useCallback((value: any) => {
    setAllFields(prev => {
      const updated = [...prev];
      updated[currentFieldIndex] = {
        ...updated[currentFieldIndex],
        value: value,
        error: undefined,
      };
      return updated;
    });
  }, [currentFieldIndex]);

  /**
   * Validate current field
   */
  const validateCurrentField = useCallback((): boolean => {
    const currentField = allFields[currentFieldIndex];
    
    if (!currentField) return true;
    
    // Check if required field is empty
    if (currentField.field.required) {
      const value = currentField.value;
      
      if (value === undefined || value === null || value === '') {
        setAllFields(prev => {
          const updated = [...prev];
          updated[currentFieldIndex] = {
            ...updated[currentFieldIndex],
            error: `${currentField.field.label} is required`,
          };
          return updated;
        });
        return false;
      }
      
      // Check for empty arrays
      if (Array.isArray(value) && value.length === 0) {
        setAllFields(prev => {
          const updated = [...prev];
          updated[currentFieldIndex] = {
            ...updated[currentFieldIndex],
            error: `${currentField.field.label} is required`,
          };
          return updated;
        });
        return false;
      }
    }
    
    return true;
  }, [allFields, currentFieldIndex]);

  /**
   * Handle next field
   */
  const handleNextField = useCallback(() => {
    if (!validateCurrentField()) {
      return;
    }
    
    if (currentFieldIndex < allFields.length - 1) {
      setCurrentFieldIndex(prev => prev + 1);
    }
  }, [currentFieldIndex, allFields.length, validateCurrentField]);

  /**
   * Handle previous field
   */
  const handlePreviousField = useCallback(() => {
    if (currentFieldIndex > 0) {
      setCurrentFieldIndex(prev => prev - 1);
    }
  }, [currentFieldIndex]);

  /**
   * Build sections data from field answers
   */
  const buildSectionsData = useCallback((): sectionDataObject[] => {
    if (!selectedBlueprint || !selectedBlueprint.data || !Array.isArray(selectedBlueprint.data)) {
      return [];
    }
    
    // Group fields back into sections
    const sections: sectionDataObject[] = selectedBlueprint.data.map(section => ({
      ...section,
      fields: [],
    }));
    
    allFields.forEach(fieldWithAnswer => {
      const section = sections.find(s => s.id === fieldWithAnswer.sectionId);
      if (section) {
        section.fields.push({
          ...fieldWithAnswer.field,
          value: fieldWithAnswer.value,
        });
      }
    });
    
    return sections;
  }, [selectedBlueprint, allFields]);

  /**
   * Handle final submission
   */
  const handleSubmit = useCallback(async () => {
    if (!validateCurrentField()) {
      return;
    }
    
    if (!selectedGame?.id || !selectedBlueprint) {
      toast({
        title: 'Error',
        description: 'Missing required data to create object',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const sectionsData = buildSectionsData();
      
      const newObject = await createObject(
        {
          game_id: selectedGame.id,
          name: objectName.trim(),
          blueprint_id: selectedBlueprint.id,
          type: 'Library Object',
          data: sectionsData,
          tags: [],
          identifiers: [],
          folder: folderFromUrl || null,
        },
        toast
      );

      await loadData();

      toast({
        title: 'Success',
        description: `${selectedCategory?.name || 'Object'} created successfully!`,
      });

      navigate('/library');
    } catch (error) {
      console.error('Error creating object:', error);
      toast({
        title: 'Error',
        description: 'Failed to create object. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateCurrentField,
    selectedGame,
    selectedBlueprint,
    selectedCategory,
    objectName,
    folderFromUrl,
    buildSectionsData,
    toast,
    loadData,
    navigate,
  ]);

  /**
   * Handle back navigation from name step
   */
  const handleBackFromName = useCallback(() => {
    setCurrentStep('category');
    setObjectName('');
    setNameError('');
  }, []);

  /**
   * Handle back navigation from fields step
   */
  const handleBackFromFields = useCallback(() => {
    if (currentFieldIndex > 0) {
      handlePreviousField();
    } else {
      setCurrentStep('name');
    }
  }, [currentFieldIndex, handlePreviousField]);

  // Show loading state if no game is selected yet
  if (!selectedGame?.id) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }

  // Get current field for rendering
  const currentField = allFields[currentFieldIndex];
  const isLastField = currentFieldIndex === allFields.length - 1;

  return (
    <div className="h-full w-full bg-surface flex flex-col">
      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center py-12 px-4 overflow-y-auto">
        <div className="w-full max-w-5xl">
          {/* Category Selection Step */}
          {currentStep === 'category' && (
            <div className="animate-fade-in">
              <CategorySelection
                categories={categories}
                onSelectCategory={handleCategorySelect}
                isLoading={false}
              />
            </div>
          )}

          {/* Name Input Step */}
          {currentStep === 'name' && (
            <div className="w-full max-w-3xl mx-auto animate-fade-in">
              <div className="mb-12 text-center">
                <h1 className="text-4xl font-bold mb-4">
                  Name your {selectedCategory?.name || 'object'}
                </h1>
                <p className="text-lg text-muted-foreground">
                  Choose a descriptive name that will help you find it later
                </p>
              </div>

              <div className="mb-8">
                <Input
                  type="text"
                  value={objectName}
                  onChange={(e) => setObjectName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleNameNext();
                    }
                  }}
                  placeholder={`Enter ${selectedCategory?.name || 'object'} name...`}
                  className="text-2xl h-16 px-6"
                  autoFocus
                />
                {nameError && (
                  <p className="text-red-600 mt-3 text-sm">{nameError}</p>
                )}
              </div>

              <FormNavigation
                showBack={true}
                showNext={true}
                showSubmit={false}
                backDisabled={false}
                nextDisabled={!objectName.trim()}
                onBack={handleBackFromName}
                onNext={handleNameNext}
                onSubmit={() => {}}
              />
            </div>
          )}

          {/* Field-by-Field Step */}
          {currentStep === 'fields' && allFields.length > 0 && currentField && (
            <div className="animate-fade-in">
              {/* Progress Bar */}
              <div className="mb-12">
                <FormProgressBar
                  current={currentFieldIndex + 1}
                  total={allFields.length}
                />
              </div>

              {/* Current Field */}
              <SingleFieldDisplay
                field={currentField.field}
                value={currentField.value}
                onChange={handleFieldChange}
                error={currentField.error}
                autoFocus={true}
              />

              {/* Navigation */}
              <FormNavigation
                showBack={true}
                showNext={!isLastField}
                showSubmit={isLastField}
                backDisabled={false}
                nextDisabled={false}
                submitDisabled={false}
                onBack={handleBackFromFields}
                onNext={handleNextField}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            </div>
          )}

          {/* No fields fallback */}
          {currentStep === 'fields' && allFields.length === 0 && (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" text="Loading fields..." />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
